import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { reportText as tr, reportLocale } from '../utils/reportTranslation';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import type { CheckInData } from '../types';
import { locColor } from '../constants/reportColors';
import { parseReportWallClock } from '../utils/reportDateTime';

interface CheckInTimelineProps {
  checkIns: CheckInData[];
  activeIdx: number;
  onSelect: (idx: number) => void;
}

const parseDate = (value?: string | null): Date | null => {
  return parseReportWallClock(value);
};

const formatTime = (date: Date | null, locale: string): string => {
  if (!date) return '';
  return date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const formatDate = (date: Date | null, locale: string): string => {
  if (!date) return '';
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const CheckInTimeline: React.FC<CheckInTimelineProps> = ({ checkIns, activeIdx, onSelect }) => {
  useTranslation('report');
  const locale = reportLocale();
  const points = useMemo(() => {
    return checkIns.map((ci, idx) => {
      const raw = ci.date ?? ci.dateDisplayServer;
      const d = parseDate(raw);
      const loc = typeof ci.levelOfConcern === 'number' ? ci.levelOfConcern : 5;
      return {
        idx,
        color: locColor(loc),
        loc,
        eCheckLabel: `eCheck ${idx + 1}`,
        dateLabel: formatDate(d, locale),
        timeLabel: formatTime(d, locale),
      };
    });
  }, [checkIns, locale]);

  if (!checkIns || checkIns.length <= 1) {
    return null;
  }

  const canOpenPrevious = activeIdx > 0;
  const canOpenNext = activeIdx < checkIns.length - 1;
  const selectAvailableReport = (index: number) => {
    if (index < 0 || index >= checkIns.length) return;
    onSelect(index);
  };

  return (
    <div
      className="checkin-timeline"
      style={{
        position: 'sticky',
        top: 'var(--topbar-height, 112px)',
        zIndex: 20,
        background: 'rgba(253, 252, 248, 0.97)',
        backdropFilter: 'saturate(140%) blur(6px)',
        WebkitBackdropFilter: 'saturate(140%) blur(6px)',
        borderBottom: '1px solid var(--border, #E2DCE8)',
        padding: '14px 16px 18px',
      }}
    >
      <div
        aria-label={tr('eCheck progression controls')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          maxWidth: 1100,
          margin: '0 auto 12px',
        }}
      >
        <button
          type="button"
          onClick={() => selectAvailableReport(activeIdx - 1)}
          disabled={!canOpenPrevious}
          aria-label={tr('Open previous eCheck')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--border, #E2DCE8)',
            borderRadius: 999,
            background: '#fff',
            color: 'var(--navy, #1C2B4A)',
            cursor: !canOpenPrevious ? 'not-allowed' : 'pointer',
            fontSize: 12,
            fontWeight: 700,
            opacity: !canOpenPrevious ? 0.4 : 1,
            padding: '7px 11px',
          }}
        >
          <FaChevronLeft aria-hidden="true" />
          {tr('Previous')}
        </button>
        <span style={{ color: 'var(--navy, #1C2B4A)', fontSize: 12, fontWeight: 700 }}>
          eCheck {tr('{{current}} of {{total}}', { current: activeIdx + 1, total: checkIns.length })}
        </span>
        <button
          type="button"
          onClick={() => selectAvailableReport(activeIdx + 1)}
          disabled={!canOpenNext}
          aria-label={tr('Open next eCheck')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--border, #E2DCE8)',
            borderRadius: 999,
            background: '#fff',
            color: 'var(--navy, #1C2B4A)',
            cursor: !canOpenNext ? 'not-allowed' : 'pointer',
            fontSize: 12,
            fontWeight: 700,
            opacity: !canOpenNext ? 0.4 : 1,
            padding: '7px 11px',
          }}
        >
          {tr('Next')}
          <FaChevronRight aria-hidden="true" />
        </button>
      </div>
      <div
        role="tablist"
        aria-label={tr('eCheck timeline')}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0,
          maxWidth: 1100,
          margin: '0 auto',
          overflowX: 'auto',
        }}
      >
        {points.map((p, i) => {
          const isActive = i === activeIdx;
          const dotSize = isActive ? 22 : 16;
          const lineColor = points[i - 1]?.color ?? p.color;
          return (
            <React.Fragment key={p.idx}>
              {i > 0 && (
                <div
                  aria-hidden="true"
                  style={{
                    flex: '1 1 auto',
                    minWidth: 36,
                    height: 3,
                    marginTop: 10,
                    background: lineColor,
                    opacity: 0.35,
                    borderRadius: 2,
                  }}
                />
              )}
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={tr('{{label}}, {{date}}, level of concern {{value}}', { label: p.eCheckLabel, date: `${p.dateLabel} ${p.timeLabel}`, value: p.loc })}
                onClick={() => selectAvailableReport(p.idx)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                  minWidth: 72,
                }}
              >
                <span
                  style={{
                    width: dotSize,
                    height: dotSize,
                    borderRadius: '50%',
                    background: p.color,
                    boxShadow: isActive
                      ? `0 0 0 4px rgba(28, 43, 74, 0.12), 0 1px 4px rgba(0,0,0,0.18)`
                      : '0 1px 2px rgba(0,0,0,0.15)',
                    transition: 'all 120ms ease',
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--navy, #1C2B4A)',
                    lineHeight: 1.1,
                  }}
                >
                  {p.eCheckLabel}
                </span>
                {p.dateLabel && (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--muted, #6B6B68)',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.dateLabel}
                  </span>
                )}
                {p.timeLabel && (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--muted, #6B6B68)',
                      lineHeight: 1.1,
                    }}
                  >
                    {p.timeLabel}
                  </span>
                )}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: p.color,
                    lineHeight: 1.1,
                  }}
                >
                  LOC {p.loc}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CheckInTimeline;
